import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, UserRole } from '../types';
import { supabase } from '../lib/supabaseClient';
import { AppSettingsService } from '../services/settings';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<{ error: any }>;
  loginWithGoogle: (role: UserRole) => Promise<{ error: any }>;
  register: (email: string, password: string, metadata: any) => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  logout: () => Promise<void>;
  updateProfile: (updates: Partial<User>) => Promise<{ error: any }>;
  updateUserEmail: (newEmail: string) => Promise<{ error: any; daysRemaining?: number }>;
  isAuthenticated: boolean;
  isLoading: boolean;
  isLandlordEnabled: boolean;
  toggleLandlordFeature: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLandlordEnabled, setIsLandlordEnabled] = useState(true);

  // Map Supabase session user/profile to our app User type
  const mapSupabaseUser = (sbUser: any, profileData?: any): User | null => {
    if (!sbUser) return null;

    // Metadata from auth (for new users) or profile table (for existing)
    const metadata = sbUser.user_metadata || {};

    return {
      id: sbUser.id,
      email: sbUser.email || '',
      name: profileData?.name || metadata.name || metadata.full_name || 'User',
      role: (profileData?.role as UserRole) || (metadata.role as UserRole) || UserRole.STUDENT,
      status: (profileData?.status) || 'ACTIVE',
      avatar: profileData?.avatar_url || metadata.avatar_url || `https://ui-avatars.com/api/?name=${profileData?.name || metadata.name || 'User'}`,
      phoneNumber: profileData?.phone_number || metadata.phone_number || '',
      institution: profileData?.institution || metadata.institution,
      agencyName: profileData?.agency_name || metadata.agency_name,
      licenseNumber: profileData?.license_number || metadata.license_number,
      propertyCount: profileData?.property_count || metadata.property_count,
      joinedDate: sbUser.created_at || new Date().toISOString(),
      lastEmailChange: profileData?.last_email_change,
    };
  };

  const fetchProfile = async (sessionUser: any) => {
    if (!sessionUser) return null;

    try {
      const fetchWithTimeout = async () => {
        const promise = supabase
          .from('profiles')
          .select('*')
          .eq('id', sessionUser.id)
          .single();

        const timeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Profile fetch timeout')), 5000)
        );
        return Promise.race([promise, timeout]) as Promise<any>;
      };

      const { data, error } = await fetchWithTimeout();

      if (error && error.code !== 'PGRST116') { // PGRST116 is not found, which is semi-expected for new users
        console.warn('Profile fetch error:', error);
      }

      if (data) {
        return mapSupabaseUser(sessionUser, data);
      }
    } catch (e) {
      console.warn('Profile fetch failed or timed out, using auth metadata');
    }
    return mapSupabaseUser(sessionUser);
  };

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      // 1. Get initial session
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (mounted && session?.user) {
          console.log('[Auth] Restoring session:', session.user.email);

          // Fetch settings first
          const settings = await AppSettingsService.getSettings();
          if (mounted) setIsLandlordEnabled(settings.landlord_feature_enabled);

          const mapped = await fetchProfile(session.user);

          // Check if user is Landlord and feature is disabled
          if (mapped && mapped.role === UserRole.LANDLORD && !settings.landlord_feature_enabled) {
            console.warn("Landlord feature disabled. Logging out user.");
            await supabase.auth.signOut();
            if (mounted) {
              setUser(null);
              setIsLoading(false);
            }
            return;
          }

          if (mounted) setUser(mapped);
        }
      } catch (err) {
        console.error('[Auth] Startup error:', err);
      } finally {
        if (mounted) setIsLoading(false);
      }

      // 2. Set up listener
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
        console.log(`[Auth] Event: ${event}`, session?.user?.email);

        if (!mounted) return;

        if (session?.user) {
          // If we already have a user and IDs match, we might skip re-fetch unless it's USER_UPDATED
          // But simpler is safer: re-fetch.
          const mapped = await fetchProfile(session.user);
          if (mounted) {
            setUser(mapped);
            setIsLoading(false);
          }
        } else {
          if (mounted) {
            setUser(null);
            // Only verify loading done if we aren't in the middle of initial init
            // But since init runs parallel, we just ensure loading is false
            setIsLoading(false);
          }
        }
      });

      return subscription;
    };

    const setup = initializeAuth();

    return () => {
      mounted = false;
      setup.then(sub => sub?.unsubscribe());
    };
  }, []);

  const login = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (!error && data.user) {
      // Post-login check
      const settings = await AppSettingsService.getSettings();
      setIsLandlordEnabled(settings.landlord_feature_enabled);

      // We need to fetch the profile to know the role, or rely on metadata if available immediately
      // Ideally we wait for the onAuthStateChange, but that might be async.
      // Let's check metadata from the response user object
      const role = data.user.user_metadata?.role as UserRole;
      // Or better, let the onStateChange handle the kick-out, BUT the user wants to be prevented "The login... I should not be able to login".
      // Returning an error here gives immediate feedback.

      // Note: Supabase might not return full profile data here if it's in a separate table, 
      // but our `register` puts role in metadata.
      if (role === UserRole.LANDLORD && !settings.landlord_feature_enabled) {
        await supabase.auth.signOut();
        return { error: { message: 'Landlord login is currently disabled by administrator.' } };
      }
    }

    return { error };
  };

  const loginWithGoogle = async (role: UserRole) => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
        redirectTo: window.location.origin
      },
    });
    return { error };
  };

  const register = async (email: string, password: string, metadata: any) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
      },
    });
    return { error };
  };

  const resetPassword = async (email: string) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/login', // Redirect back to login after clicking email link
    });
    return { error };
  };

  const updateProfile = async (updates: Partial<User>) => {
    if (!user) return { error: { message: 'No user logged in' } };

    console.log("Updating profile for:", user.id, updates);

    try {
      // 1. Update Public Profiles Table (The Source of Truth for App Data)
      // Wrappping in timeout to prevent hanging
      const dbUpdatePromise = supabase
        .from('profiles')
        .update({
          name: updates.name,
          phone_number: updates.phoneNumber,
          institution: updates.institution,
          agency_name: updates.agencyName,
          license_number: updates.licenseNumber,
          property_count: updates.propertyCount,
          avatar_url: updates.avatar,
        })
        .eq('id', user.id);

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Update request timed out. Check your internet or try again.')), 8000)
      );

      const result: any = await Promise.race([dbUpdatePromise, timeoutPromise]);

      if (result.error) {
        console.error("DB Update Error", result.error);
        return { error: result.error };
      }

      // 2. Update Local State Optimistically
      setUser(prev => prev ? { ...prev, ...updates } : null);

      // 3. Update Supabase Auth Metadata (Secondary, "Fire and Forget")
      const authUpdates: any = {
        data: {
          name: updates.name,
          institution: updates.institution,
          agency_name: updates.agencyName,
        }
      };

      // Non-blocking auth update
      supabase.auth.updateUser(authUpdates).then(({ error }) => {
        if (error) console.warn("Auth Metadata Update Warning:", error);
      });

      return { error: null };

    } catch (e: any) {
      console.error("Critical Profile Update Error:", e);
      return { error: e };
    }
  };

  const logout = async () => {
    try {
      console.log('[Auth] Logging out user...');
      await supabase.auth.signOut();
      console.log('[Auth] Logout successful');
    } catch (error) {
      console.error('[Auth] Logout error:', error);
    } finally {
      // Force UI logout even if server fails
      setUser(null);
    }
  };

  const updateUserEmail = async (newEmail: string) => {
    if (!user) return { error: { message: 'No user logged in' } };

    // Check 30-day restriction
    if (user.lastEmailChange) {
      const lastChange = new Date(user.lastEmailChange).getTime();
      const daysSinceChange = (Date.now() - lastChange) / (1000 * 60 * 60 * 24);

      if (daysSinceChange < 30) {
        const daysRemaining = Math.ceil(30 - daysSinceChange);
        return {
          error: { message: `You can change your email again in ${daysRemaining} days` },
          daysRemaining
        };
      }
    }

    try {
      // 1. Update Supabase Auth email (with timeout)
      const updateAuthWithTimeout = async () => {
        const promise = supabase.auth.updateUser({ email: newEmail });
        const timeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Auth update timed out. Please check your connection.')), 15000)
        );
        return Promise.race([promise, timeout]) as Promise<any>;
      };

      const { error: authError } = await updateAuthWithTimeout();
      if (authError) throw authError;

      // 2. Update profiles table (with timeout)
      const updateProfileWithTimeout = async () => {
        const promise = supabase
          .from('profiles')
          .update({
            email: newEmail,
            last_email_change: new Date().toISOString()
          })
          .eq('id', user.id);

        const timeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Database update timed out.')), 10000)
        );
        return Promise.race([promise, timeout]) as Promise<any>;
      };

      const { error: dbError } = await updateProfileWithTimeout();
      if (dbError) throw dbError;

      // 3. Refresh user data locally
      setUser(prev => prev ? {
        ...prev,
        email: newEmail,
        lastEmailChange: new Date().toISOString()
      } : null);

      return { error: null };
    } catch (error: any) {
      console.error('[Auth] Email update error:', error);
      return { error: { message: error.message || 'An unexpected error occurred' } };
    }
  };

  const toggleLandlordFeature = async () => {
    const newState = !isLandlordEnabled;
    setIsLandlordEnabled(newState);
    await AppSettingsService.updateSetting('landlord_feature_enabled', newState);
  };

  return (
    <AuthContext.Provider value={{ user, login, loginWithGoogle, register, resetPassword, logout, updateProfile, updateUserEmail, isAuthenticated: !!user, isLoading, isLandlordEnabled, toggleLandlordFeature }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};